import React, { useState, useEffect, useContext } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./Points.css";
import { AuthContext } from "../AuthContext";
import { CartContext } from "../CartContext";
import Swal from "sweetalert2";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Points() {
  const [pointsBalance, setPointsBalance] = useState(0);
  const [pointsLoading, setPointsLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showTransactionModal, setShowTransactionModal] = useState(false);
  const [transactionTypeFilter, setTransactionTypeFilter] = useState("all");
  const [dateRangeFilter, setDateRangeFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);

  const [redeemableProducts, setRedeemableProducts] = useState([]);
  const [filteredRedeemableProducts, setFilteredRedeemableProducts] = useState([]);
  const [redeemableProductsLoading, setRedeemableProductsLoading] = useState(true);
  const [categories, setCategories] = useState([]);

  const [nameFilter, setNameFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sortBy, setSortBy] = useState("name_asc");
  const [redeemQuantities, setRedeemQuantities] = useState({});

  const [voucherTemplates, setVoucherTemplates] = useState([]);
  const [filteredVoucherTemplates, setFilteredVoucherTemplates] = useState([]);
  const [voucherTemplatesLoading, setVoucherTemplatesLoading] = useState(true);

  const [userVouchers, setUserVouchers] = useState([]);
  const [filteredUserVouchers, setFilteredUserVouchers] = useState([]);
  const [userVouchersLoading, setUserVouchersLoading] = useState(true);

  const [voucherNameFilter, setVoucherNameFilter] = useState("");
  const [voucherSortBy, setVoucherSortBy] = useState("points_asc");
  const [userVoucherStatusFilter, setUserVoucherStatusFilter] = useState("all");
  const [activeMainTab, setActiveMainTab] = useState("products");
  const [activeVoucherTab, setActiveVoucherTab] = useState("available");
  const [expiringPoints, setExpiringPoints] = useState(null);
  const [expiringPointsLoading, setExpiringPointsLoading] = useState(true);

  const navigate = useNavigate();
  const { userInfo, isLoggedIn, authFetch } = useContext(AuthContext);
  const { addToCart } = useContext(CartContext);

  const getTransactionType = (type) => {
    const typeMap = {
      1: { text: "Earned", className: "transaction-earned", icon: "fas fa-plus-circle" },
      2: { text: "Spent", className: "transaction-spent", icon: "fas fa-minus-circle" },
      3: { text: "Expired", className: "transaction-expired", icon: "fas fa-clock" },
      4: { text: "Refunded", className: "transaction-refunded", icon: "fas fa-undo" }
    };
    return typeMap[type] || { text: "Unknown", className: "transaction-unknown", icon: "fas fa-question" };
  };



  const fetchPointsBalance = async () => {
    if (!isLoggedIn || !userInfo || userInfo.role !== "user") {
      return;
    }

    try {
      setPointsLoading(true);
      const response = await authFetch(`${API_BASE_URL}/api/Points/balance`);

      if (response.ok) {
        const data = await response.json();
        setPointsBalance(data.balance || 0);
      } else if (response.status === 404) {
        setPointsBalance(0);
      } else {
        console.error('Failed to fetch points balance');
        setPointsBalance(0);
      }
    } catch (error) {
      console.error('Error fetching points balance:', error);
      setPointsBalance(0);
    } finally {
      setPointsLoading(false);
    }
  };

  const fetchExpiringPoints = async () => {
    if (!isLoggedIn || !userInfo || userInfo.role !== "user") {
      return;
    }

    try {
      setExpiringPointsLoading(true);
      const response = await authFetch(`${API_BASE_URL}/api/Points/expiring?daysAhead=365`);

      if (response.ok) {
        const data = await response.json();
        const expiringTransactions = data.expiringTransactions || [];

        if (expiringTransactions.length > 0) {
          const sortedExpiring = expiringTransactions.sort((a, b) =>
            new Date(a.expiryDate) - new Date(b.expiryDate)
          );

          const oldestDate = sortedExpiring[0].expiryDate;
          const sameDate = sortedExpiring.filter(transaction =>
            transaction.expiryDate === oldestDate
          );

          const totalExpiringPoints = sameDate.reduce((sum, transaction) =>
            sum + transaction.remainingPoints, 0
          );

          setExpiringPoints({
            remainingPoints: totalExpiringPoints,
            expiryDate: oldestDate
          });
        } else {
          setExpiringPoints(null);
        }
      } else if (response.status === 404) {
        setExpiringPoints(null);
      } else {
        console.error('Failed to fetch expiring points');
        setExpiringPoints(null);
      }
    } catch (error) {
      console.error('Error fetching expiring points:', error);
      setExpiringPoints(null);
    } finally {
      setExpiringPointsLoading(false);
    }
  };

  const fetchTransactionHistory = async (page = 1) => {
    if (!isLoggedIn || !userInfo || userInfo.role !== "user") {
      return;
    }

    try {
      setTransactionsLoading(true);
      const response = await authFetch(
        `${API_BASE_URL}/api/Points/history?page=${page}&pageSize=${transactionsPerPage}`
      );

      if (response.ok) {
        const data = await response.json();
        setTransactions(data.transactions || []);
        setCurrentPage(data.currentPage || 1);
        const hasMorePages = data.transactions.length === transactionsPerPage;
        setTotalPages(hasMorePages ? Math.max(page + 1, totalPages) : page);
      } else if (response.status === 404) {
        setTransactions([]);
        setTotalPages(1);
      } else {
        throw new Error('Failed to fetch points history');
      }
    } catch (error) {
      console.error('Error fetching points history:', error);
      setError('Failed to load points history');
    } finally {
      setTransactionsLoading(false);
    }
  };

  const fetchRedeemableProducts = async () => {
    try {
      setRedeemableProductsLoading(true);
      const response = await fetch(`${API_BASE_URL}/products/redeemable`);
      if (!response.ok) {
        throw new Error("Failed to fetch redeemable products");
      }
      const data = await response.json();
      const sortedProducts = [...data.products].sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      setRedeemableProducts(sortedProducts);
      setFilteredRedeemableProducts(sortedProducts);
    } catch (error) {
      console.error('Error fetching redeemable products:', error);
      setError('Failed to load redeemable products');
    } finally {
      setRedeemableProductsLoading(false);
    }
  };

  const fetchVoucherTemplates = async () => {
    try {
      setVoucherTemplatesLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/VoucherTemplates/active`);
      if (!response.ok) {
        throw new Error("Failed to fetch voucher templates");
      }
      const data = await response.json();
      const sortedTemplates = [...data].sort((a, b) =>
        a.name.localeCompare(b.name)
      );

      setVoucherTemplates(sortedTemplates);
      setFilteredVoucherTemplates(sortedTemplates);
    } catch (error) {
      console.error('Error fetching voucher templates:', error);
      setError('Failed to load voucher templates');
    } finally {
      setVoucherTemplatesLoading(false);
    }
  };

  const fetchUserVouchers = async () => {
    if (!isLoggedIn || !userInfo || userInfo.role !== "user") {
      return;
    }

    try {
      setUserVouchersLoading(true);
      const response = await authFetch(`${API_BASE_URL}/api/UserVouchers`);

      if (response.ok) {
        const data = await response.json();
        setUserVouchers(data || []);
        setFilteredUserVouchers(data || []);
      } else if (response.status === 404) {
        setUserVouchers([]);
        setFilteredUserVouchers([]);
      } else {
        console.error('Failed to fetch user vouchers');
        setUserVouchers([]);
        setFilteredUserVouchers([]);
      }
    } catch (error) {
      console.error('Error fetching user vouchers:', error);
      setUserVouchers([]);
      setFilteredUserVouchers([]);
    } finally {
      setUserVouchersLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/categories`);
      if (!response.ok) {
        throw new Error("Failed to fetch categories");
      }
      const data = await response.json();
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const formatDescription = (description) => {
    if (!description) return 'N/A';

    return description.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line}
        {index < description.split('\n').length - 1 && <br />}
      </React.Fragment>
    ));
  };

  const applyFilters = () => {
    let filtered = [...transactions];

    if (transactionTypeFilter !== "all") {
      const typeNumber = parseInt(transactionTypeFilter);
      filtered = filtered.filter(transaction => transaction.transactionType === typeNumber);
    }

    if (dateRangeFilter === "last7") {
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
      filtered = filtered.filter(
        transaction => new Date(transaction.createdAt) >= sevenDaysAgo
      );
    } else if (dateRangeFilter === "last30") {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      filtered = filtered.filter(
        transaction => new Date(transaction.createdAt) >= thirtyDaysAgo
      );
    } else if (dateRangeFilter === "last90") {
      const ninetyDaysAgo = new Date();
      ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
      filtered = filtered.filter(
        transaction => new Date(transaction.createdAt) >= ninetyDaysAgo
      );
    }

    setFilteredTransactions(filtered);
  };

  const applyRedeemableProductFilters = () => {
    if (!redeemableProducts.length) return;

    let filtered = [...redeemableProducts];
    if (nameFilter) {
      filtered = filtered.filter((product) =>
        product.name.toLowerCase().includes(nameFilter.toLowerCase())
      );
    }

    if (categoryFilter) {
      filtered = filtered.filter(
        (product) => product.categoryID === parseInt(categoryFilter)
      );
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "popularity_asc":
          return (a.totalRedemptions || 0) - (b.totalRedemptions || 0);
        case "popularity_desc":
          return (b.totalRedemptions || 0) - (a.totalRedemptions || 0);
        case "points_asc":
          return (a.pointsRequired || 0) - (b.pointsRequired || 0);
        case "points_desc":
          return (b.pointsRequired || 0) - (a.pointsRequired || 0);
        default:
          return a.name.localeCompare(b.name);
      }
    });

    setFilteredRedeemableProducts(filtered);
  };

  const applyVoucherTemplateFilters = () => {
    if (!voucherTemplates.length) return;

    let filtered = [...voucherTemplates];

    if (voucherNameFilter) {
      filtered = filtered.filter((template) =>
        template.name.toLowerCase().includes(voucherNameFilter.toLowerCase())
      );
    }

    filtered.sort((a, b) => {
      switch (voucherSortBy) {
        case "name_asc":
          return a.name.localeCompare(b.name);
        case "name_desc":
          return b.name.localeCompare(a.name);
        case "points_asc":
          return (a.pointsRequired || 0) - (b.pointsRequired || 0);
        case "points_desc":
          return (b.pointsRequired || 0) - (a.pointsRequired || 0);
        case "discount_asc":
          return (a.discountAmount || 0) - (b.discountAmount || 0);
        case "discount_desc":
          return (b.discountAmount || 0) - (a.discountAmount || 0);
        default:
          return a.name.localeCompare(b.name);
      }
    });

    setFilteredVoucherTemplates(filtered);
  };

  const applyUserVoucherFilters = () => {
    if (!userVouchers.length) return;

    let filtered = [...userVouchers];

    if (userVoucherStatusFilter !== "all") {
      filtered = filtered.filter((voucher) => {
        switch (userVoucherStatusFilter) {
          case "available":
            return voucher.status === "Available";
          case "used":
            return voucher.status === "Used";
          case "expired":
            return voucher.status === "Expired";
          default:
            return true;
        }
      });
    }

    filtered.sort((a, b) => new Date(b.redeemedAt) - new Date(a.redeemedAt));

    setFilteredUserVouchers(filtered);
  };

  const resetTransactionFilters = () => {
    setTransactionTypeFilter("all");
    setDateRangeFilter("all");
    setFilteredTransactions(transactions);
  };

  const clearRedeemableFilters = () => {
    setNameFilter("");
    setCategoryFilter("");
    setSortBy("name_asc");
  };

  const clearVoucherFilters = () => {
    setVoucherNameFilter("");
    setVoucherSortBy("points_asc");
  };

  const clearUserVoucherFilters = () => {
    setUserVoucherStatusFilter("all");
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchTransactionHistory(page);
  };

  // Number of bundles (redemptions) the customer selected
  const getRedeemQty = (productId) => redeemQuantities[productId] || 1;

  // What one redemption delivers, e.g. "1 bag" or "2 bag". Always shown, including
  // for a redeem quantity of 1, so the customer knows exactly what they get.
  const redeemUnitsLabel = (product) => {
    const units = product.redeemQuantity || 1;
    const uom = product.uom || (units === 1 ? 'unit' : 'units');
    return `${units} ${uom}`;
  };

  // Max bundles available = stock / units-per-bundle
  const getMaxBundles = (product) => {
    const perBundle = product.redeemQuantity || 1;
    return Math.floor((product.stockQuantity || 0) / perBundle);
  };

  const changeRedeemQty = (product, delta) => {
    setRedeemQuantities((prev) => {
      const current = prev[product.productID] || 1;
      const maxBundles = getMaxBundles(product);
      let next = current + delta;
      if (next < 1) next = 1;
      if (maxBundles > 0 && next > maxBundles) next = maxBundles;
      return { ...prev, [product.productID]: next };
    });
  };

  const handleRedeemProduct = async (product) => {
    if (!userInfo) {
      Swal.fire({
        title: 'Login Required',
        text: 'Please login to redeem products.',
        icon: 'info',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });
      return;
    }

    // Always redeem exactly 1 bundle from this page. Customers can adjust the
    // quantity later in the cart.
    const bundles = 1;
    const perBundle = product.redeemQuantity || 1;              // units per redemption
    const totalUnits = bundles * perBundle;                     // actual units delivered
    const totalPointsRequired = product.pointsRequired * bundles;

    if (pointsBalance < totalPointsRequired) {
      Swal.fire({
        title: 'Insufficient Points',
        text: `You need ${totalPointsRequired} points to redeem this. You currently have ${pointsBalance} points.`,
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });
      return;
    }

    // Cart quantity = number of bundles (1). Points are charged per bundle; the backend
    // delivers (bundles x redeemQuantity) units and deducts stock accordingly.
    addToCart({
      ...product,
      selectedVariant: null,
      price: 0,
      isRedeemable: true
    }, bundles);

    Swal.fire({
      title: 'Added to Cart!',
      text: `${totalUnits} ${product.uom || 'unit(s)'} of ${product.name} added to cart (${totalPointsRequired} points). Points will be deducted when you place the order.`,
      icon: 'success',
      timer: 3000,
      showConfirmButton: false
    });
  };

  const handleRedeemVoucher = async (template) => {
    if (!userInfo) {
      Swal.fire({
        title: 'Login Required',
        text: 'Please login to redeem vouchers.',
        icon: 'info',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });
      return;
    }

    if (pointsBalance < template.pointsRequired) {
      Swal.fire({
        title: 'Insufficient Points',
        text: `You need ${template.pointsRequired} points to redeem this voucher. You currently have ${pointsBalance} points.`,
        icon: 'warning',
        confirmButtonText: 'OK',
        confirmButtonColor: '#3085d6',
      });
      return;
    }

    const result = await Swal.fire({
      title: 'Redeem Voucher',
      html: `
        <div style="text-align: left; margin: 1rem 0;">
          <p><strong>Voucher:</strong> ${template.name}</p>
          <p><strong>Discount:</strong> ${template.isPercentage ? template.discountAmount + '%' : 'RM ' + template.discountAmount.toFixed(2)}</p>
          ${template.minimumSpendAmount ? `<p><strong>Minimum Spend:</strong> RM ${template.minimumSpendAmount.toFixed(2)}</p>` : ''}
          <p><strong>Points Required:</strong> ${template.pointsRequired}</p>
          <p><strong>Expiry:</strong> ${template.expiryDays || 30} days from redemption</p>
        </div>
        <p>Are you sure you want to redeem this voucher?</p>
      `,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Redeem Now',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#28a745',
      cancelButtonColor: '#6c757d',
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      const response = await authFetch(`${API_BASE_URL}/api/UserVouchers/redeem/${template.templateId}`, {
        method: 'POST',
      });

      if (response.ok) {
        const data = await response.json();
        
        await fetchPointsBalance();
        await fetchUserVouchers();

        Swal.fire({
          title: 'Voucher Redeemed!',
          html: `
            <div style="text-align: center;">
              <p>Your voucher code:</p>
              <div style="font-family: monospace; font-size: 1.5rem; font-weight: bold; color: #28a745; background: #f8f9fa; padding: 0.5rem; border-radius: 0.25rem; margin: 1rem 0;">
                ${data.voucherCode}
              </div>
              <p style="font-size: 0.9rem; color: #666;">
                Expires on ${formatExpiryDate(data.expiryDate)}
              </p>
            </div>
          `,
          icon: 'success',
          confirmButtonText: 'OK',
          confirmButtonColor: '#28a745',
        });
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to redeem voucher');
      }
    } catch (error) {
      console.error('Error redeeming voucher:', error);
      Swal.fire({
        title: 'Redemption Failed',
        text: error.message || 'An error occurred while redeeming the voucher. Please try again.',
        icon: 'error',
        confirmButtonText: 'OK',
        confirmButtonColor: '#dc3545',
      });
    }
  };

  const copyVoucherCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      Swal.fire({
        title: 'Copied!',
        text: 'Voucher code copied to clipboard',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    }).catch(() => {
      const textArea = document.createElement('textarea');
      textArea.value = code;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      
      Swal.fire({
        title: 'Copied!',
        text: 'Voucher code copied to clipboard',
        icon: 'success',
        timer: 1500,
        showConfirmButton: false
      });
    });
  };

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const formatExpiryDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getVoucherStatusClass = (status) => {
    switch (status) {
      case "Available":
        return "voucher-available";
      case "Used":
        return "voucher-used";
      case "Expired":
        return "voucher-expired";
      default:
        return "voucher-unknown";
    }
  };

  const getVoucherStatusIcon = (status) => {
    switch (status) {
      case "Available":
        return "fas fa-check-circle";
      case "Used":
        return "fas fa-shopping-cart";
      case "Expired":
        return "fas fa-clock";
      default:
        return "fas fa-question";
    }
  };

  useEffect(() => {
    if (!isLoggedIn || !userInfo) {
      navigate("/login");
      return;
    }

    if (userInfo.role === "user") {
      fetchPointsBalance();
      fetchExpiringPoints();
      fetchRedeemableProducts();
      fetchVoucherTemplates();
      fetchUserVouchers();
      fetchCategories();
    }
  }, [navigate, userInfo, isLoggedIn]);

  useEffect(() => {
    applyFilters();
  }, [transactions, transactionTypeFilter, dateRangeFilter]);

  useEffect(() => {
    applyRedeemableProductFilters();
  }, [redeemableProducts, nameFilter, categoryFilter, sortBy]);

  useEffect(() => {
    applyVoucherTemplateFilters();
  }, [voucherTemplates, voucherNameFilter, voucherSortBy]);

  useEffect(() => {
    applyUserVoucherFilters();
  }, [userVouchers, userVoucherStatusFilter]);

  const openTransactionModal = () => {
    setShowTransactionModal(true);
    if (transactions.length === 0) {
      fetchTransactionHistory();
    }
  };

  const closeTransactionModal = () => {
    setShowTransactionModal(false);
  };

  if (error) {
    return (
      <div className="points-container">
        <div className="points-sub-container">
          <div className="points-error">
            <h3>Something went wrong</h3>
            <p>We're having trouble loading your points information. Please try again later.</p>
            <button
              className="points-retry-btn"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="points-container">
      <div className="points-sub-container">
        {/* Header Section */}
        <div className="points-header">
          <div className="points-welcome-section">
            <h1 className="points-welcome">Rewards Catalogue</h1>
            <p className="points-subtitle">
              Redeem exclusive products & vouchers with your points
            </p>
          </div>
          <div className="points-actions">
            <div className="points-balance-display">
              <div className="points-icon">
                <i className="fas fa-coins"></i>
              </div>
              <div className="points-info">
                <div className="points-label">Your Points</div>
                <div className="points-value">
                  {pointsLoading ? (
                    <div className="points-loading">
                      <i className="fas fa-spinner fa-spin"></i>
                    </div>
                  ) : (
                    <span>{pointsBalance.toLocaleString()}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="points-actions-right">
              <div className="points-buttons-row">
                <button
                  className="points-history-btn"
                  onClick={openTransactionModal}
                >
                  <i className="fas fa-history"></i>Points History
                </button>
                <Link to="/dashboard" className="points-back-btn">
                  <i className="fas fa-arrow-left"></i>Back to Dashboard
                </Link>
              </div>

              {/* Expiring Points Warning */}
              {expiringPointsLoading ? (
                <div className="points-expiry-warning loading">
                  <i className="fas fa-spinner fa-spin"></i>
                  <span>Loading expiring points information...</span>
                </div>
              ) : expiringPoints ? (
                <div className="points-expiry-warning">
                  <i className="fas fa-clock"></i>
                  <span>
                    {expiringPoints.remainingPoints.toLocaleString()} points expiring on {formatExpiryDate(expiringPoints.expiryDate)}
                  </span>
                </div>
              ) : (
                <div className="points-expiry-warning no-expiry">
                  <i className="fas fa-check-circle"></i>
                  <span>No points expiring soon</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="points-content">
          {/* Main Redemption Section with Tabs */}
          <div className="points-section">
            <h2 className="points-section-title">
              <i className="fas fa-gift"></i>
              Rewards Catalogue
            </h2>

            {/* Main Tab Navigation */}
            <div className="main-tabs">
              <button
                className={`main-tab ${activeMainTab === "products" ? "active" : ""}`}
                onClick={() => setActiveMainTab("products")}
              >
                <i className="fas fa-boxes"></i>
                Products
              </button>
              <button
                className={`main-tab ${activeMainTab === "vouchers" ? "active" : ""}`}
                onClick={() => setActiveMainTab("vouchers")}
              >
                <i className="fas fa-ticket-alt"></i>
                Vouchers
              </button>
            </div>

            {/* Products Tab Content */}
            {activeMainTab === "products" && (
              <div className="main-tab-content">
                {/* Filters */}
                <div className="points-filter">
                  <div className="filter-group">
                    <label className="filter-label">Search</label>
                    <input
                      type="text"
                      placeholder="Filter by product name"
                      value={nameFilter}
                      onChange={(e) => setNameFilter(e.target.value)}
                      className="filter-input"
                    />
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">Category</label>
                    <select
                      className="filter-select"
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                    >
                      <option value="">All Categories</option>
                      {categories
                        .filter(category => !category.subCategories || category.subCategories.length === 0)
                        .map((category) => (
                          <option key={category.categoryID} value={category.categoryID}>
                            {category.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div className="filter-group">
                    <label className="filter-label">Sort By</label>
                    <select
                      className="filter-select"
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                    >
                      <option value="name_asc">Name (A-Z)</option>
                      <option value="name_desc">Name (Z-A)</option>
                      <option value="popularity_desc">Most Popular</option>
                      <option value="popularity_asc">Least Popular</option>
                      <option value="points_asc">Points (Low to High)</option>
                      <option value="points_desc">Points (High to Low)</option>
                    </select>
                  </div>

                  <button className="filter-button" onClick={clearRedeemableFilters}>
                    Search
                  </button>
                </div>

                {/* Product Grid */}
                {redeemableProductsLoading ? (
                  <div className="points-loading-container">
                    <div className="loading-spinner"></div>
                    <p>Loading redeemable products...</p>
                  </div>
                ) : filteredRedeemableProducts.length > 0 ? (
                  <div className="redeemable-products-grid">
                    {filteredRedeemableProducts.map((product) => (
                      <div key={product.productID} className="redeemable-product-card">
                        <div className="redeemable-product-image-container">
                          <img
                            src={product.mainImageUrl}
                            alt={product.name}
                            className="redeemable-product-image"
                          />
                        </div>
                        <div className="redeemable-product-details">
                          <h3 className="redeemable-product-name">{product.name}</h3>
                          <div className="redeemable-product-points">
                            <i className="fas fa-coins"></i>
                            {product.pointsRequired} Points
                            <span className="redeemable-product-bundle">
                              {' '}= {redeemUnitsLabel(product)}
                            </span>
                          </div>
                          <div className="redeemable-product-info">
                            <div className="redeemable-product-detail">
                              <span className="redeemable-product-detail-label">Product Code:</span>
                              <span className="redeemable-product-detail-value">
                                {product.productCode || 'N/A'}
                              </span>
                            </div>
                            <div className="redeemable-product-detail">
                              <span className="redeemable-product-detail-label">Category:</span>
                              <span className="redeemable-product-detail-value">
                                {categories.find(cat => cat.categoryID === product.categoryID)?.name || 'Unknown'}
                              </span>
                            </div>
                            <div className="redeemable-product-detail">
                              <span className="redeemable-product-detail-label">Status:</span>
                              <span className="redeemable-product-detail-value">
                                {product.stockQuantity > 0 ? (
                                  <span className="in-stock">In Stock</span>
                                ) : (
                                  <span className="out-of-stock">Out of Stock</span>
                                )}
                              </span>
                            </div>
                          </div>
                          {product.description && (
                            <p className="redeemable-product-description">
                              {formatDescription(product.description)}
                            </p>
                          )}
                        </div>
                        <div className="redeemable-product-actions">
                          {getMaxBundles(product) > 0 ? (
                            <>
                              <div className="redeemable-product-units-note">
                                You will get {redeemUnitsLabel(product)}
                              </div>

                              <div className="redeemable-product-total-points">
                                <i className="fas fa-coins"></i>
                                Total: {product.pointsRequired} Points
                              </div>

                              {pointsBalance >= product.pointsRequired ? (
                                <button
                                  className="redeemable-product-btn-redeem"
                                  onClick={() => handleRedeemProduct(product)}
                                >
                                  <i className="fas fa-gift"></i>
                                  Redeem Now
                                </button>
                              ) : (
                                <button className="redeemable-product-btn-redeem" disabled style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                                  Insufficient Points
                                </button>
                              )}
                            </>
                          ) : (
                            <button className="redeemable-product-btn-redeem" disabled style={{ opacity: 0.5, cursor: 'not-allowed' }}>
                              Out of Stock
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="points-empty">
                    <i className="fas fa-gift"></i>
                    <p>No redeemable products available</p>
                    <p>Check back later for new redemption items!</p>
                  </div>
                )}
              </div>
            )}

            {/* Vouchers Tab Content */}
            {activeMainTab === "vouchers" && (
              <div className="main-tab-content">
                {/* Voucher Sub-Tab Navigation */}
                <div className="voucher-tabs">
                  <button
                    className={`voucher-tab ${activeVoucherTab === "available" ? "active" : ""}`}
                    onClick={() => setActiveVoucherTab("available")}
                  >
                    <i className="fas fa-store"></i>
                    Available Vouchers
                  </button>
                  <button
                    className={`voucher-tab ${activeVoucherTab === "my-vouchers" ? "active" : ""}`}
                    onClick={() => setActiveVoucherTab("my-vouchers")}
                  >
                    <i className="fas fa-wallet"></i>
                    My Vouchers
                  </button>
                </div>

                {/* Available Vouchers Sub-Tab */}
                {activeVoucherTab === "available" && (
                  <div className="voucher-tab-content">
                    {/* Voucher Filters */}
                    <div className="points-filter">
                      <div className="filter-group">
                        <label className="filter-label">Search</label>
                        <input
                          type="text"
                          placeholder="Filter by voucher name"
                          value={voucherNameFilter}
                          onChange={(e) => setVoucherNameFilter(e.target.value)}
                          className="filter-input"
                        />
                      </div>

                      <div className="filter-group">
                        <label className="filter-label">Sort By</label>
                        <select
                          className="filter-select"
                          value={voucherSortBy}
                          onChange={(e) => setVoucherSortBy(e.target.value)}
                        >
                          <option value="name_asc">Name (A-Z)</option>
                          <option value="name_desc">Name (Z-A)</option>
                          <option value="points_asc">Points (Low to High)</option>
                          <option value="points_desc">Points (High to Low)</option>
                          <option value="discount_asc">Discount (Low to High)</option>
                          <option value="discount_desc">Discount (High to Low)</option>
                        </select>
                      </div>

                      <button className="filter-button" onClick={clearVoucherFilters}>
                       Search
                      </button>
                    </div>

                    {/* Voucher Templates Grid */}
                    {voucherTemplatesLoading ? (
                      <div className="points-loading-container">
                        <div className="loading-spinner"></div>
                        <p>Loading voucher templates...</p>
                      </div>
                    ) : filteredVoucherTemplates.length > 0 ? (
                      <div className="redeemable-products-grid">
                        {filteredVoucherTemplates.map((template) => (
                          <div key={template.templateId} className="voucher-template-card">
                            <div className="voucher-template-header">
                              <div className="voucher-template-discount">
                                {template.isPercentage 
                                  ? `${template.discountAmount}% OFF`
                                  : `RM ${template.discountAmount.toFixed(2)} OFF`
                                }
                              </div>
                              <div className="voucher-template-points">
                                <i className="fas fa-coins"></i>
                                {template.pointsRequired} Points
                              </div>
                            </div>
                            <div className="voucher-template-details">
                              <h3 className="voucher-template-name">{template.name}</h3>
                              <p className="voucher-template-description">
                                {formatDescription(template.description)}
                              </p>
                              <div className="voucher-template-info">
                                {template.minimumSpendAmount && (
                                  <div className="voucher-template-detail">
                                    <span className="voucher-template-detail-label">Minimum Spend:</span>
                                    <span className="voucher-template-detail-value">
                                      RM {template.minimumSpendAmount.toFixed(2)}
                                    </span>
                                  </div>
                                )}
                                <div className="voucher-template-detail">
                                  <span className="voucher-template-detail-label">Expiry:</span>
                                  <span className="voucher-template-detail-value">
                                    {template.expiryDays || 30} days
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="voucher-template-actions">
                              {pointsBalance >= template.pointsRequired && (
                                <button
                                  className="voucher-template-btn-redeem"
                                  onClick={() => handleRedeemVoucher(template)}
                                >
                                  <i className="fas fa-ticket-alt"></i>
                                  Redeem Voucher
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="points-empty">
                        <i className="fas fa-ticket-alt"></i>
                        <p>No vouchers available for redemption</p>
                        <p>Check back later for new voucher offers!</p>
                      </div>
                    )}
                  </div>
                )}

                {/* My Vouchers Sub-Tab */}
                {activeVoucherTab === "my-vouchers" && (
                  <div className="voucher-tab-content">
                    {/* User Voucher Filters */}
                    <div className="points-filter">
                      <div className="filter-group">
                        <label className="filter-label">Status</label>
                        <select
                          className="filter-select"
                          value={userVoucherStatusFilter}
                          onChange={(e) => setUserVoucherStatusFilter(e.target.value)}
                        >
                          <option value="all">All Status</option>
                          <option value="available">Available</option>
                          <option value="used">Used</option>
                          <option value="expired">Expired</option>
                        </select>
                      </div>

                      <button className="filter-button" onClick={clearUserVoucherFilters}>
                        Search
                      </button>
                    </div>

                    {/* User Vouchers Grid */}
                    {userVouchersLoading ? (
                      <div className="points-loading-container">
                        <div className="loading-spinner"></div>
                        <p>Loading your vouchers...</p>
                      </div>
                    ) : filteredUserVouchers.length > 0 ? (
                      <div className="user-vouchers-grid">
                        {filteredUserVouchers.map((voucher) => (
                          <div key={voucher.userVoucherId} className={`user-voucher-card ${getVoucherStatusClass(voucher.status)}`}>
                            <div className="user-voucher-header">
                              <div className="user-voucher-status">
                                <i className={getVoucherStatusIcon(voucher.status)}></i>
                                {voucher.status}
                              </div>
                              <div className="user-voucher-discount">
                                {voucher.isPercentage 
                                  ? `${voucher.discountAmount}% OFF`
                                  : `RM ${voucher.discountAmount.toFixed(2)} OFF`
                                }
                              </div>
                            </div>
                            <div className="user-voucher-details">
                              <h3 className="user-voucher-name">{voucher.templateName}</h3>
                              <div className="user-voucher-code-container">
                                <div className="user-voucher-code" onClick={() => copyVoucherCode(voucher.voucherCode)}>
                                  <span>{voucher.voucherCode}</span>
                                  <i className="fas fa-copy"></i>
                                </div>
                              </div>
                              <div className="user-voucher-info">
                                {voucher.minimumSpendAmount && (
                                  <div className="user-voucher-detail">
                                    <span className="user-voucher-detail-label">Minimum Spend:</span>
                                    <span className="user-voucher-detail-value">
                                      RM {voucher.minimumSpendAmount.toFixed(2)}
                                    </span>
                                  </div>
                                )}
                                <div className="user-voucher-detail">
                                  <span className="user-voucher-detail-label">Redeemed:</span>
                                  <span className="user-voucher-detail-value">
                                    {formatExpiryDate(voucher.redeemedAt)}
                                  </span>
                                </div>
                                <div className="user-voucher-detail">
                                  <span className="user-voucher-detail-label">Expires:</span>
                                  <span className="user-voucher-detail-value">
                                    {formatExpiryDate(voucher.expiryDate)}
                                  </span>
                                </div>
                                {voucher.status === "Used" && voucher.usedAt && (
                                  <div className="user-voucher-detail">
                                    <span className="user-voucher-detail-label">Used:</span>
                                    <span className="user-voucher-detail-value">
                                      {formatExpiryDate(voucher.usedAt)}
                                    </span>
                                  </div>
                                )}
                                {voucher.status === "Used" && voucher.orderNum && (
                                  <div className="user-voucher-detail">
                                    <span className="user-voucher-detail-label">Order:</span>
                                    <span className="user-voucher-detail-value">
                                      #{voucher.orderNum}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="points-empty">
                        <i className="fas fa-wallet"></i>
                        <p>No vouchers in your inventory</p>
                        <p>Redeem vouchers from the Available Vouchers tab to start building your collection!</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Transaction History Modal */}
      {showTransactionModal && (
        <div className="modal-overlay" onClick={closeTransactionModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-history"></i>
                Points History
              </h2>
              <button className="modal-close" onClick={closeTransactionModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              {/* Filters */}
              <div className="points-filter">
                <div className="filter-group">
                  <label className="filter-label">Transaction Type</label>
                  <select
                    className="filter-select"
                    value={transactionTypeFilter}
                    onChange={(e) => setTransactionTypeFilter(e.target.value)}
                  >
                    <option value="all">All Types</option>
                    <option value="1">Earned</option>
                    <option value="2">Spent</option>
                    <option value="3">Expired</option>
                    <option value="4">Refunded</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label className="filter-label">Date Range</label>
                  <select
                    className="filter-select"
                    value={dateRangeFilter}
                    onChange={(e) => setDateRangeFilter(e.target.value)}
                  >
                    <option value="all">All Time</option>
                    <option value="last7">Last 7 Days</option>
                    <option value="last30">Last 30 Days</option>
                    <option value="last90">Last 90 Days</option>
                  </select>
                </div>

                <button className="filter-button" onClick={resetTransactionFilters}>
                 Search
                </button>
              </div>

              {/* Transaction List */}
              {transactionsLoading ? (
                <div className="points-loading-container">
                  <div className="loading-spinner"></div>
                  <p>Loading points history...</p>
                </div>
              ) : filteredTransactions.length > 0 ? (
                <>
                  <div className="points-transactions-container">
                    <table className="points-transactions-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Type</th>
                          <th>Description</th>
                          <th>Points</th>
                          <th>Balance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTransactions.map((transaction) => {
                          const transactionType = getTransactionType(transaction.transactionType);
                          return (
                            <tr key={transaction.transactionId}>
                              <td className="transaction-date">
                                {formatDate(transaction.createdAt)}
                              </td>
                              <td>
                                <span className={`transaction-type ${transactionType.className}`}>
                                  <i className={transactionType.icon}></i>
                                  {transactionType.text}
                                </span>
                              </td>
                              <td className="transaction-description" title={transaction.description}>
                                {transaction.description}
                              </td>
                              <td className={`transaction-points ${transaction.points >= 0 ? 'positive' : 'negative'}`}>
                                {transaction.points >= 0 ? '+' : ''}{transaction.points.toLocaleString()}
                              </td>
                              <td className="transaction-balance-after">
                                {transaction.balanceAfter?.toLocaleString() || '0'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="points-pagination">
                      <button
                        className="points-pagination-btn"
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                      >
                        <i className="fas fa-angle-double-left"></i>
                      </button>
                      <button
                        className="points-pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <i className="fas fa-angle-left"></i>
                      </button>

                      {[...Array(Math.min(totalPages, 5)).keys()].map((number) => {
                        const pageNumber = Math.max(1, currentPage - 2) + number;
                        if (pageNumber <= totalPages) {
                          return (
                            <button
                              key={pageNumber}
                              className={`points-pagination-btn ${currentPage === pageNumber ? "active" : ""}`}
                              onClick={() => handlePageChange(pageNumber)}
                            >
                              {pageNumber}
                            </button>
                          );
                        }
                        return null;
                      })}

                      <button
                        className="points-pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <i className="fas fa-angle-right"></i>
                      </button>
                      <button
                        className="points-pagination-btn"
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        <i className="fas fa-angle-double-right"></i>
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="points-empty">
                  <i className="fas fa-receipt"></i>
                  <p>No transactions found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Points;